import { Routes } from '@angular/router';
import { AvailablePermissions } from '@app/roles/models/available-permissions';
import { authGuard } from '@app/security/guards/auth.guard';
import { featureGuard, FeatureGuardObject } from '@app/security/guards/feature.guard';
import { permissionGuardChild } from '@app/security/guards/permission.guard';
import { RouteConstants } from '@app/shared/models/route-constants';
import { BreadcrumbObject } from '@app/shared/models/shared.models';
import { myProficiencyLevelsLoadedGuard } from '@app/tenants/guards/my-proficiency-levels-loaded.guard';
import { myTenantSettingsLoadedGuard } from '@app/tenants/guards/my-tenant-settings-loaded.guard';
import { TenantFeature } from '@app/tenants/models/tenant-feature';

export const routes: Routes = [
	{
		path: 'landing',
		canActivate: [authGuard],
		loadChildren: () =>
			import('@app/landing/landing.routes')
				.then(m => m.routes)
	},
	{
		path: 'users',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.administration:Administration`,
				disable: true
			}
		},
		canActivateChild: [permissionGuardChild],
		loadChildren: () =>
			import('@app/users/users.routes')
				.then(m => m.routes)
	},
	{
		path: 'overview',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: 'MyKnow',
				disable: true
			}
		},
		loadChildren: () =>
			import('@app/overview/overview.routes')
				.then(m => m.routes)
	},
	{
		path: 'reporting',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.reporting:Reporting`,
				disable: true
			}
		},
		loadChildren: () =>
			import('@app/reporting/reporting.routes')
				.then(m => m.routes)
	},
	{
		path: 'data-trace',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.administration:Administration`,
				disable: true
			}
		},
		loadChildren: () =>
			import('@app/data-trace/data-trace.routes')
				.then(m => m.routes)
	},
	{
		path: 'teams',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.administration:Administration`,
				disable: true
			}
		},
		loadChildren: () =>
			import('@app/teams/teams.routes')
				.then(m => m.routes)
	},
	{
		path: 'authenticate',
		loadChildren: () =>
			import('@app/security/security.routes')
				.then(m => m.routes)
	},
	{
		path: 'tenants',
		canActivate: [authGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.administration:Administration`,
				disable: true
			},
			allowedPermissions: [
				AvailablePermissions.SearchTenants,
				AvailablePermissions.AddTenant,
				AvailablePermissions.EditTenant,
				AvailablePermissions.DeleteTenant,
				AvailablePermissions.ReadTenant
			]
		},
		loadChildren: () =>
			import('@app/tenants/tenants.routes')
				.then(m => m.routes)
	},
	{
		path: 'roles',
		canActivate: [authGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.administration:Administration`,
				disable: true
			},
			allowedPermissions: [
				AvailablePermissions.SearchRoles,
				AvailablePermissions.AddRole,
				AvailablePermissions.EditRole,
				AvailablePermissions.DeleteRole,
				AvailablePermissions.ReadRole
			]
		},
		loadChildren: () =>
			import('@app/roles/roles.routes')
				.then(m => m.routes)
	},
	{
		path: 'support',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.support:Support`,
				disable: true
			}
		},
		loadChildren: () =>
			import('@app/support/support.routes')
				.then(m => m.routes)
	},
	{
		path: 'error',
		loadChildren: () =>
			import('@app/error-pages/error-pages.routes')
				.then(m => m.routes)
	},
	{
		path: 'competency-library',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.competencies:Competencies`,
				disable: true
			},
			featureGuard: [TenantFeature.Competencies]
		},
		loadChildren: () =>
			import('@app/competencies/competencies.routes')
				.then(m => m.routes)
	},
	{
		path: 'job-families',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.administration:Administration`,
				disable: true
			},
			featureGuard: [TenantFeature.JobFamilies]
		},
		loadChildren: () =>
			import('@app/job-profile/job-profile.routes')
				.then(m => m.routes)
	},
	{
		path: 'message-templates',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.communication:Communication`,
				disable: true
			},
			featureGuard: [TenantFeature.Messages]
		},
		loadChildren: () =>
			import('@app/message-templates/message-templates.routes')
				.then(m => m.routes)
	},
	{
		path: 'skills-passport-manager',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.competencies:Competencies`,
				disable: true
			},
			featureGuard: [TenantFeature.CompetenciesSkillsPassport]
		},
		loadChildren: () =>
			import('@app/skills-passport-manager/skills-passport-manager.routes')
				.then(m => m.routes)
	},
	{
		path: 'settings',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.settings:Settings`,
				disable: true
			}
		},
		loadChildren: () =>
			import('@app/settings/settings.routes')
				.then(m => m.routes)
	},
	{
		path: 'activities',
		canActivate: [authGuard, myTenantSettingsLoadedGuard, myProficiencyLevelsLoadedGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: 'MyKnow',
				disable: true
			},
			featureGuard: <FeatureGuardObject>{
				features: [TenantFeature.AssessmentScheduling, TenantFeature.Competencies],
				match: 'some'
			}
		},
		loadChildren: () =>
			import('@app/activities/activities.routes')
				.then(m => m.routes)
	},
	{
		path: 'assessment-schedules',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.assessments:Assessments`,
				disable: true
			},
			featureGuard: [TenantFeature.AssessmentScheduling]
		},
		loadChildren: () =>
			import('@app/master-activities/master-activities.routes')
				.then(m => m.routes)
	},
	{
		path: 'media-manager',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.media-manager:Media manager`
			},
			featureGuard: [TenantFeature.MediaManager]
		},
		loadChildren: () =>
			import('@app/media-manager/media-manager.routes')
				.then(m => m.routes)
	},
	{
		path: 'competencies',
		canActivate: [authGuard, myTenantSettingsLoadedGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: 'MyKnow',
				disable: true
			},
			featureGuard: [TenantFeature.Competencies]
		},
		loadChildren: () =>
			import('@app/tracker/tracker.routes')
				.then(m => m.routes)
	},
	{
		path: 'assessor-forms',
		canActivate: [authGuard, myTenantSettingsLoadedGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: 'MyKnow',
				disable: true
			},
			featureGuard: [TenantFeature.Competencies]
		},
		loadChildren: () =>
			import('@app/assessor-forms/assessor-forms.routes')
				.then(m => m.routes)
	},
	{
		path: 'skills-passports',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: 'MyKnow',
				disable: true
			},
			featureGuard: [TenantFeature.CompetenciesSkillsPassport]
		},
		loadChildren: () =>
			import('@app/skills-passports/skills-passports.routes')
				.then(m => m.routes)
	},
	{
		path: 'career-progress',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.workforce:Workforce`,
				disable: true
			},
			featureGuard: [
				TenantFeature.Competencies,
				TenantFeature.CompetenciesCareerProgress
			]
		},
		loadChildren: () =>
			import('@app/career-progress/career-progress.routes')
				.then(m => m.routes)
	},
	{
		path: 'question-bank',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.assessments:Assessments`,
				disable: true
			},
			featureGuard: [TenantFeature.Assessments]
		},
		loadChildren: () =>
			import('@app/question-bank/question-bank.routes')
				.then(m => m.routes)
	},
	{
		path: 'assessment-library',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.assessments:Assessments`,
				disable: true
			},
			featureGuard: [TenantFeature.Assessments]
		},
		loadChildren: () =>
			import('@app/assessments/assessments.routes')
				.then(m => m.routes)
	},
	{
		path: 'assessments',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: 'MyKnow',
				disable: true
			},
			featureGuard: [TenantFeature.Assessments]
		},
		loadChildren: () =>
			import('@app/mk-assessments/mk-assessments.routes')
				.then(m => m.routes)
	},
	{
		path: 'take',
		canActivate: [featureGuard],
		data: {
			featureGuard: [TenantFeature.Assessments]
		},
		loadChildren: () =>
			import('@app/take/take.routes')
				.then(m => m.routes)
	},
	{
		path: 'dashboards',
		canActivate: [authGuard, featureGuard],
		canActivateChild: [permissionGuardChild],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.dashboards:Dashboards`,
				disable: true
			},
			featureGuard: [TenantFeature.Dashboards]
		},
		loadChildren: () =>
			import('@app/dashboards/dashboards.routes')
				.then(m => m.routes)
	},
	{
		path: 'messages',
		canActivate: [authGuard, featureGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.messages:Messages`,
				disable: true
			},
			featureGuard: [TenantFeature.Messages]
		},
		loadChildren: () =>
			import('@app/messages/messages.routes')
				.then(m => m.routes)
	},
	{
		path: 'notifications',
		canActivate: [authGuard],
		data: {
			breadcrumb: <BreadcrumbObject>{
				label: () => $localize`:@@navigation.notifications:Notifications`
			}
		},
		loadChildren: () =>
			import('@app/notification/notification.routes')
				.then(m => m.routes)
	},
	{
		path: 'data-transfer',
		canActivate: [authGuard],
		loadChildren: () =>
			import('@app/data-transfer/data-transfer.routes')
				.then(m => m.routes)
	},
	{
		path: 'email-history',
		canActivate: [authGuard],
		loadChildren: () =>
			import('@app/email-history/email-history.routes')
				.then(m => m.routes)
	},
	{ path: '', redirectTo: 'landing', pathMatch: 'full' },
	{ path: '**', redirectTo: RouteConstants.Error404, pathMatch: 'full' }
];

